import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import {
  getPlatformTenant,
  getPlatformTenantUsers,
  getSelectedPlatformTenant,
  listPlatformTenants,
  mapLaravelError,
  selectPlatformTenant,
} from '../../services/platformTenantsApi';
import { useNavigate } from 'react-router-dom';
import {
  formatLicenseSummary,
  formatLicenseWindow,
  licenseStatusColor,
  licenseStatusLabel,
} from '../../utils/tenantLicense';

const TENANT_BASE_PATH = '/super-admin/platform/tenants';

const statusColor = (status) => {
  const value = String(status || '').toLowerCase();
  if (value === 'active') return 'success';
  if (value === 'suspended') return 'warning';
  if (value === 'archived') return 'default';
  return 'default';
};

const MetricCard = ({ icon, label, value, helper }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      ) : null}
    </Stack>
  </Paper>
);

const DetailLine = ({ label, value }) => (
  <Stack spacing={0.35}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value || '-'}
    </Typography>
  </Stack>
);

export default function PlatformDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(() => {
    const stored = getSelectedPlatformTenant();
    return stored?.id ? Number(stored.id) : null;
  });
  const [selectedTenantPayload, setSelectedTenantPayload] = useState(null);
  const [selectedUsersPayload, setSelectedUsersPayload] = useState(null);

  const selectedTenantSummary = useMemo(
    () => tenants.find((tenant) => tenant.id === selectedTenantId) || null,
    [selectedTenantId, tenants]
  );

  const loadTenants = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const payload = await listPlatformTenants();
      const rows = Array.isArray(payload) ? payload : [];
      setTenants(rows);

      const storedTenant = getSelectedPlatformTenant();
      if (!storedTenant) {
        return;
      }

      const matchedTenant = rows.find((tenant) => String(tenant.id) === String(storedTenant.id))
        || rows.find((tenant) => tenant.key === storedTenant.key);

      if (matchedTenant) {
        setSelectedTenantId(matchedTenant.id);
      }
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    if (!selectedTenantId) {
      setSelectedTenantPayload(null);
      setSelectedUsersPayload(null);
      setDetailError('');
      return;
    }

    let active = true;

    const loadSelectedTenant = async () => {
      setDetailLoading(true);
      setDetailError('');

      try {
        const [tenantPayload, usersPayload] = await Promise.all([
          getPlatformTenant(selectedTenantId),
          getPlatformTenantUsers(selectedTenantId, { limit: 8 }),
        ]);

        if (!active) {
          return;
        }

        setSelectedTenantPayload(tenantPayload || null);
        setSelectedUsersPayload(usersPayload || null);

        const persistedTenant = tenantPayload?.tenant || selectedTenantSummary;
        if (persistedTenant) {
          selectPlatformTenant(persistedTenant);
        }
      } catch (requestError) {
        if (!active) {
          return;
        }

        setSelectedTenantPayload(null);
        setSelectedUsersPayload(null);
        setDetailError(mapLaravelError(requestError).message);
      } finally {
        if (active) {
          setDetailLoading(false);
        }
      }
    };

    loadSelectedTenant();

    return () => {
      active = false;
    };
  }, [selectedTenantId, selectedTenantSummary]);

  const summaryMetrics = useMemo(() => {
    return tenants.reduce((accumulator, tenant) => {
      const users = tenant?.metrics?.users || {};
      const licenseUsage = tenant?.license_usage || {};

      accumulator.totalTenants += 1;
      if (String(tenant?.status || '').toLowerCase() === 'active') {
        accumulator.activeTenants += 1;
      }

      if (Number.isFinite(users.total)) {
        accumulator.totalUsers += users.total;
      }

      if (Number.isFinite(users.active)) {
        accumulator.activeUsers += users.active;
      }

      if (Number.isFinite(licenseUsage.limit)) {
        accumulator.licensedSeats += licenseUsage.limit;
      }

      return accumulator;
    }, {
      totalTenants: 0,
      activeTenants: 0,
      totalUsers: 0,
      activeUsers: 0,
      licensedSeats: 0,
    });
  }, [tenants]);

  const selectedTenant = selectedTenantPayload?.tenant || selectedTenantSummary;
  const selectedMetrics = selectedTenantPayload?.metrics || selectedTenantSummary?.metrics || null;
  const selectedLicenseUsage = selectedTenantPayload?.license_usage || selectedTenantSummary?.license_usage || null;
  const selectedLicenseStatus = selectedTenantPayload?.license_status || selectedTenantSummary?.license_status || '';
  const selectedCurrentLicense = selectedTenantPayload?.current_license || selectedTenantSummary?.current_license || null;
  const selectedNextLicense = selectedTenantPayload?.next_license || selectedTenantSummary?.next_license || null;
  const selectedUsers = Array.isArray(selectedUsersPayload?.data) ? selectedUsersPayload.data : [];

  const handleSelectTenant = (tenant) => {
    if (!tenant?.id) {
      return;
    }

    selectPlatformTenant(tenant);
    setSelectedTenantId(tenant.id);
  };

  const handleManageUsers = (tenant = selectedTenant) => {
    if (!tenant?.id) {
      return;
    }

    handleSelectTenant(tenant);
    navigate('/admin/users');
  };

  const handleOpenTenant = (tenant = selectedTenant) => {
    if (!tenant?.id) {
      return;
    }

    navigate(`${TENANT_BASE_PATH}/${tenant.id}`);
  };

  const handleOpenTenantAdmin = (tenant = selectedTenant) => {
    if (!tenant?.id) {
      return;
    }

    handleSelectTenant(tenant);
    navigate('/admin/dashboard');
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack spacing={0.5}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Super Admin Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a tenant, review the dated license window and seat usage, then move into that tenant&apos;s admin workspace.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DomainOutlinedIcon />}
              onClick={() => navigate(TENANT_BASE_PATH)}
            >
              Tenant Management
            </Button>
            <Button
              variant="contained"
              startIcon={<ManageAccountsOutlinedIcon />}
              onClick={() => handleManageUsers()}
              disabled={!selectedTenant}
            >
              Manage Users
            </Button>
          </Stack>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {detailError ? <Alert severity="warning">{detailError}</Alert> : null}

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <MetricCard
              icon={<DashboardOutlinedIcon color="primary" fontSize="small" />}
              label="Tenants"
              value={summaryMetrics.totalTenants}
              helper={`${summaryMetrics.activeTenants} active`}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MetricCard
              icon={<CheckCircleOutlinedIcon color="success" fontSize="small" />}
              label="Active Tenants"
              value={summaryMetrics.activeTenants}
              helper="Available for selection"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MetricCard
              icon={<GroupsOutlinedIcon color="primary" fontSize="small" />}
              label="Provisioned Users"
              value={summaryMetrics.totalUsers}
              helper={`${summaryMetrics.activeUsers} active users consuming seats`}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MetricCard
              icon={<ManageAccountsOutlinedIcon color="warning" fontSize="small" />}
              label="Configured Seats"
              value={summaryMetrics.licensedSeats || 'Not set'}
              helper="Sum of current active-seat caps"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} xl={5}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Selected Tenant
                </Typography>

                {!selectedTenant ? (
                  <Alert severity="info">
                    Choose a tenant below. The selected tenant becomes the active context for user management and tenant admin pages.
                  </Alert>
                ) : null}

                {detailLoading ? (
                  <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : selectedTenant ? (
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {selectedTenant.display_name}
                      </Typography>
                      <Chip size="small" color={statusColor(selectedTenant.status)} label={selectedTenant.status || 'unknown'} />
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <DetailLine label="Tenant key" value={selectedTenant.key} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DetailLine label="Org code" value={selectedTenant.org_code} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DetailLine label="Product label" value={selectedTenant.product_label} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DetailLine label="Users preview" value={selectedUsersPayload?.total != null ? `${selectedUsersPayload.total} users` : '-'} />
                      </Grid>
                    </Grid>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={4}>
                        <MetricCard
                          icon={<ManageAccountsOutlinedIcon color="warning" fontSize="small" />}
                          label="Seats Used"
                          value={selectedLicenseUsage?.used ?? 'N/A'}
                          helper="Active users"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <MetricCard
                          icon={<GroupsOutlinedIcon color="primary" fontSize="small" />}
                          label="Provisioned"
                          value={selectedMetrics?.users?.total ?? 'N/A'}
                          helper="All user records"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <MetricCard
                          icon={<DomainOutlinedIcon color="success" fontSize="small" />}
                          label="License Cap"
                          value={selectedLicenseUsage?.limit ?? 'Not set'}
                          helper={selectedLicenseUsage?.remaining != null ? `${selectedLicenseUsage.remaining} seats remaining` : 'No current seat cap'}
                        />
                      </Grid>
                    </Grid>

                    <Alert severity={licenseStatusColor(selectedLicenseStatus) === 'error' ? 'error' : 'info'}>
                      {selectedCurrentLicense
                        ? `Current license: ${formatLicenseWindow(selectedCurrentLicense)} (${licenseStatusLabel(selectedLicenseStatus)}). ${formatLicenseSummary(selectedLicenseUsage)}.`
                        : `No active license. ${selectedNextLicense ? `Next window starts ${selectedNextLicense.valid_from}.` : 'Create a dated license window before provisioning more users.'}`}
                    </Alert>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button variant="contained" startIcon={<ManageAccountsOutlinedIcon />} onClick={() => handleManageUsers(selectedTenant)}>
                        Manage Users
                      </Button>
                      <Button variant="outlined" startIcon={<DashboardOutlinedIcon />} onClick={() => handleOpenTenantAdmin(selectedTenant)}>
                        Tenant Admin Dashboard
                      </Button>
                      <Button variant="text" startIcon={<VisibilityOutlinedIcon />} onClick={() => handleOpenTenant(selectedTenant)}>
                        View Tenant
                      </Button>
                    </Stack>
                  </Stack>
                ) : null}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} xl={7}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Tenant User Preview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active users are treated as consumed license seats. Select a tenant to preview the latest user list before opening full management.
                </Typography>

                {!selectedTenant ? (
                  <Alert severity="info">No tenant selected yet.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Site</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedUsers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4}>
                              <Typography variant="body2" color="text.secondary">
                                No users returned for the selected tenant.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : selectedUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <Stack spacing={0.15}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {user.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {user.employee_code} {user.email ? `• ${user.email}` : ''}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.15}>
                                <Typography variant="body2">{user.role || '-'}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {user.actual_role || '-'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" color={statusColor(user.status)} label={user.status || 'unknown'} />
                            </TableCell>
                            <TableCell>{user.site || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper variant="outlined">
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Tenant Selection
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a tenant to switch the admin context. Once selected, the existing HR admin screens operate against that tenant.
            </Typography>
          </Stack>

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Users</TableCell>
                    <TableCell>License</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          No tenants found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : tenants.map((tenant) => {
                    const metrics = tenant?.metrics || {};
                    const users = metrics?.users || {};
                    const licenseUsage = tenant?.license_usage || {};
                    const licenseStatus = tenant?.license_status || '';
                    const currentLicense = tenant?.current_license || null;
                    const isSelected = tenant.id === selectedTenantId;

                    return (
                      <TableRow key={tenant.id} hover selected={isSelected}>
                        <TableCell>
                          <Stack spacing={0.2}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {tenant.display_name}
                              </Typography>
                              {isSelected ? <Chip size="small" color="primary" label="Selected" /> : null}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {tenant.key} • {tenant.org_code}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={statusColor(tenant.status)} label={tenant.status || 'unknown'} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {users.active ?? 0} active / {users.total ?? 0} total
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatLicenseSummary(licenseUsage)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {currentLicense ? formatLicenseWindow(currentLicense) : licenseStatusLabel(licenseStatus)}
                          </Typography>
                          <Chip sx={{ mt: 0.75 }} size="small" color={licenseStatusColor(licenseStatus)} label={licenseStatusLabel(licenseStatus)} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant={isSelected ? 'contained' : 'outlined'}
                              startIcon={<CheckCircleOutlinedIcon />}
                              onClick={() => handleSelectTenant(tenant)}
                            >
                              Select
                            </Button>
                            <Button
                              size="small"
                              startIcon={<ManageAccountsOutlinedIcon />}
                              onClick={() => handleManageUsers(tenant)}
                            >
                              Manage Users
                            </Button>
                            <Button
                              size="small"
                              startIcon={<LaunchOutlinedIcon />}
                              onClick={() => handleOpenTenant(tenant)}
                            >
                              Open
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
